import { Grow } from '@mui/material';
import classNames from 'classnames';
import React from 'react';
import { BiChevronDown } from 'react-icons/bi';

interface CustomDropDownProps {
	label: string;
	children?: React.ReactNode;
	options?: { label: string; value: string }[];
	buttonStyle?: React.CSSProperties;
	dropdownStyle?: React.CSSProperties;
	growProps?: Partial<React.ComponentProps<typeof Grow>>;
	onChange?: (value: string) => void;
	buttonClassName?: string;
	dropdownClassName?: string;
	optionClassName?: string;
	icon?: React.ReactNode;
	iconPosition?: 'left' | 'right';
	closeOnSelect?: boolean;
	noOptionsMessage?: React.ReactNode;
	disabled?: boolean;
}

const CustomDropDown: React.FC<CustomDropDownProps> = ({
	label,
	children,
	options,
	buttonStyle,
	dropdownStyle,
	growProps,
	onChange,
	buttonClassName = '',
	dropdownClassName = '',
	optionClassName = '',
	icon = <BiChevronDown size={20} />,
	iconPosition = 'right',
	closeOnSelect = true,
	noOptionsMessage = 'No Options',
	disabled = false,
}) => {
	const [ref, setRef] = React.useState<HTMLButtonElement | null>(null);
	const [open, setOpen] = React.useState(false);

	React.useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (ref && !ref.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, [ref]);

	const handleOptionClick = (value: string) => {
		onChange && onChange(value);
		if (closeOnSelect) setOpen(false);
	};

	return (
		<>
			<button
				type="button"
				className={classNames('flex h-full w-full items-center', buttonClassName, disabled && 'cursor-not-allowed pointer-events-none')}
				style={buttonStyle}
				onClick={() => setOpen(prev => !prev)}
				ref={setRef}
				disabled={disabled}
			>
				{iconPosition === 'left' && icon}
				{label}
				{iconPosition === 'right' && <span className="ml-[10px]">{icon}</span>}
			</button>

			<Grow in={open} unmountOnExit style={{ transformOrigin: '100% 0 0' }} {...growProps}>
				<div
					className={classNames(
						'absolute top-full right-0 w-fit bg-white rounded-lg shadow-md z-[1] py-1',
						dropdownClassName,
					)}
					style={dropdownStyle}
				>
					{children ? (
						children
					) : options?.length ? (
						options.map(option => (
							<button
								type="button"
								className={classNames(
									'text-left py-2 px-4 w-full',
									option.label?.toLowerCase() === label?.toLowerCase() ? 'bg-purple-200' : 'hover:bg-purple-100',
									optionClassName,
								)}
								key={option.value}
								onClick={() => handleOptionClick(option.value)}
							>
								{option.label}
							</button>
						))
					) : (
						<>{noOptionsMessage}</>
					)}
				</div>
			</Grow>
		</>
	);
};

export default CustomDropDown;
